import { useEffect, useState } from 'react';

import { useAsync, useDeferredAsync, usePoll } from 'hooks';
import {
  fetchPackage as fetchPackageAction,
  createPackage,
  publishPackage as publishPackageAction,
  unpublishPackage as unpublishPackageAction,
  Document,
} from 'slices/publishing';
import { Action, Item } from 'components/organisms/publish/interfaces';
import {
  hasContentsUsedInAssignments,
  getTopicNamesUsedInAssignments,
} from 'components/organisms/publish/util';

export const FETCH_PACKAGE_INTERVAL = 3000;

export interface PublishingPackage {
  id: string;
  valid: boolean;
  has_warnings: boolean;
  completed: boolean;
  reported: boolean;
  items: Item[];
}

const usePublish = (
  courseId: string,
  documents: Document[],
  documentsForTracking: Document[],
  action: Action,
) => {
  // 1. Create a package
  // 2. Start polling: fetch package every FETCH_PACKAGE_INTERVAL
  // 3. If fetched package is reported (reported: true) proceed to 4, otherwise
  //    wait for package to be fetched again and repeat 3
  // 4. If package is valid (valid: true) for (un)publishing (un)publish it,
  //    otherwise display errors and go to 7
  // 5. Polling continues: fetch package every FETCH_PACKAGE_INTERVAL
  // 6. If (un)publishing is done (completed: true) proceed to 7, otherwise wait
  //    for package to be fetched again and repeat 6
  // 7. Stop polling
  // 8. Done

  const [isPublishing, setIsPublishing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [topicNamesUsedInAssignment, setTopicNamesUsedInAssignment] = useState<
    string[]
  >([]);

  const { result: createdPackage, failed: createPackageFailed } = useAsync(
    createPackage,
    {
      courseId,
      documents,
      documentsForTracking,
      action,
    },
  );

  const {
    run: fetchPackage,
    pending: isFetchingPackage,
    result: fetchedPackage,
    failed: fetchPackageFailed,
  } = useDeferredAsync<PublishingPackage>(fetchPackageAction);

  const { run: publishPackage } = useDeferredAsync(publishPackageAction);
  const { run: unpublishPackage } = useDeferredAsync(unpublishPackageAction);

  const { startPolling, stopPolling } = usePoll({
    pollIntervalInMilliseconds: FETCH_PACKAGE_INTERVAL,
    callback: () => {
      if (createdPackage && !isFetchingPackage) {
        fetchPackage({ id: createdPackage.id });
      }
    },
  });

  useEffect(() => {
    if (createPackageFailed) {
      setHasError(true);
      stopPolling();
    }
  }, [createPackageFailed, stopPolling]);

  useEffect(() => {
    if (fetchPackageFailed) {
      setHasError(true);
      stopPolling();
    }
  }, [fetchPackageFailed, stopPolling]);

  useEffect(() => {
    if (isPublished || hasError) {
      return;
    }

    if (createdPackage) {
      startPolling();
    }
  }, [createdPackage, startPolling, isPublished, hasError]);

  useEffect(() => {
    if (
      hasError ||
      isPublishing ||
      !fetchedPackage ||
      !fetchedPackage.reported ||
      fetchedPackage.completed
    ) {
      return;
    }

    if (fetchedPackage.valid) {
      const payload = { id: fetchedPackage.id };

      action === Action.Publish || action === Action.QuickSend
        ? publishPackage(payload)
        : unpublishPackage(payload);

      setIsPublishing(true);
    } else {
      const items = fetchedPackage.items;

      if (action === Action.Unpublish && hasContentsUsedInAssignments(items)) {
        setTopicNamesUsedInAssignment(getTopicNamesUsedInAssignments(items));
      }

      setHasError(true);
      stopPolling();
    }
  }, [
    fetchedPackage,
    publishPackage,
    unpublishPackage,
    isPublishing,
    setIsPublishing,
    stopPolling,
    action,
    hasError,
    fetchPackageFailed,
  ]);

  useEffect(() => {
    if (fetchedPackage?.completed) {
      setIsPublished(true);
      stopPolling();
    }
  }, [fetchedPackage, stopPolling]);

  return { isPublished, hasError, topicNamesUsedInAssignment };
};

export default usePublish;
